import {
  correspondenceMarkCorrespondenceReadMutation,
  useHeyApi,
} from "@platform/api";
import { useMutation } from "@tanstack/react-query";

export interface MarkCorrespondenceRead {
  /** Marks one message read or unread. */
  setMessageRead: (messageId: string, read: boolean) => void;
  /** Marks every message on the thread read. */
  markThreadRead: () => void;
  isMarking: boolean;
}

/**
 * Marks correspondence read for the signed-in analyst.
 *
 * The reader is never sent: the BFF takes it from the caller's token, because
 * read state is per analyst and a body-supplied address would let one mark
 * another's queue.
 *
 * Invalidation goes through the Correspondence tag rather than a hand-built
 * key, so both the open thread and the listing behind it pick the change up —
 * the badge on the row is derived from the same count the thread returns.
 */
export const useMarkCorrespondenceRead = (
  threadId: string,
): MarkCorrespondenceRead => {
  const { centroPortalClient, invalidateQuery } = useHeyApi();

  const { mutate, isPending } = useMutation({
    ...correspondenceMarkCorrespondenceReadMutation({
      client: centroPortalClient,
    }),
    onSuccess: () => invalidateQuery({ tags: ["Correspondence"] }),
  });

  return {
    setMessageRead: (messageId, read) =>
      mutate({
        path: { threadId },
        body: { messageIds: [messageId], read },
      }),
    markThreadRead: () =>
      mutate({
        path: { threadId },
        body: { wholeThread: true, read: true },
      }),
    isMarking: isPending,
  };
};
